/**
 * Employee labor-cost burden — single source of truth.
 *
 * West Tennessee Consulting applies a fixed **20% burden** on top of each
 * employee's hourly pay rate (`app_users.hourly_rate`) when computing the
 * internal cost of work for accounting / profitability purposes. The burden
 * covers software licenses, hardware, and other per-employee operating costs
 * that aren't captured by the raw hourly wage.
 *
 * True internal labor cost of an hour of work is therefore:
 *
 *     cost = hours × employee_hourly_rate × (1 + EMPLOYEE_COST_BURDEN_RATE)
 *
 * IMPORTANT — do NOT confuse this with `tickets.total_cost`. That column is the
 * total ticket cost **to the CLIENT** (what the client is charged: billable
 * hours × `billable_rate`). It is a client-facing figure, NOT the owner's
 * internal labor cost, and must never be used for company profitability / owner
 * cost math. Internal labor cost is ALWAYS derived from the employee's pay rate
 * (`app_users.hourly_rate`) via the helpers below — never from `billable_rate`,
 * `companies.hourly_rate`, or `tickets.total_cost`.
 *
 * Any code (UI, reports, AI agents) that computes employee/labor cost for
 * accounting MUST go through these constants/helpers so the burden is applied
 * consistently and is trivial to change in one place.
 */

/** Fractional burden applied on top of employee hourly pay (0.20 = 20%). */
export const EMPLOYEE_COST_BURDEN_RATE = 0.2;

/** Multiplier form of the burden (1.20). Multiply a base cost by this. */
export const EMPLOYEE_COST_BURDEN_MULTIPLIER = 1 + EMPLOYEE_COST_BURDEN_RATE;

/** Whole-percent form for display (20). */
export const EMPLOYEE_COST_BURDEN_PERCENT = Math.round(EMPLOYEE_COST_BURDEN_RATE * 100);

/** Short label for UI badges/subtext, e.g. next to a cost figure. */
export const EMPLOYEE_COST_BURDEN_LABEL = `incl. ${EMPLOYEE_COST_BURDEN_PERCENT}% burden`;

/** Full plain-English explanation for tooltips / help text. */
export const EMPLOYEE_COST_BURDEN_DESCRIPTION =
  `West Tennessee Consulting applies a ${EMPLOYEE_COST_BURDEN_PERCENT}% burden rate on top of ` +
  `each employee's hourly pay to cover software licenses, hardware, and other ` +
  `per-employee operating costs. Internal labor cost = hourly rate × ${EMPLOYEE_COST_BURDEN_MULTIPLIER}.`;

/**
 * Apply the burden to a base hourly pay rate.
 * @returns the fully-burdened hourly cost, or 0 if the rate is missing/invalid.
 */
export function burdenedHourlyCost(hourlyRate: number | null | undefined): number {
  if (typeof hourlyRate !== "number" || !Number.isFinite(hourlyRate) || hourlyRate <= 0) {
    return 0;
  }
  return hourlyRate * EMPLOYEE_COST_BURDEN_MULTIPLIER;
}

/**
 * Compute the fully-burdened internal labor cost for a block of work.
 *
 * @param hours      hours worked
 * @param hourlyRate the employee's pay rate (`app_users.hourly_rate`)
 * @returns hours × hourlyRate × 1.20, or 0 if either input is missing/invalid.
 */
export function computeBurdenedLaborCost(
  hours: number | null | undefined,
  hourlyRate: number | null | undefined
): number {
  if (typeof hours !== "number" || !Number.isFinite(hours) || hours <= 0) {
    return 0;
  }
  const ratePerHour = burdenedHourlyCost(hourlyRate);
  if (ratePerHour <= 0) {
    return 0;
  }
  return hours * ratePerHour;
}
